import { useEffect, useState } from 'react';
import api from '../services/api';

/**
 * @module useQuestionnaires
 * @description Custom hook for fetching all questionnaires in the Mental Health Portal application.
 * Automatically fetches data when the component mounts and manages loading and error states.
 *
 * @requires react
 * @requires ../services/api
 */

/**
 * Hook to fetch and manage the list of all questionnaires
 * @function useQuestionnaires
 * @returns {Object} Object containing questionnaires array, loading state, and error state
 */
export const useQuestionnaires = () => {
  /**
   * @state {Array} questionnaires - The list of fetched questionnaires
   */
  const [questionnaires, setQuestionnaires] = useState([]);

  /**
   * @state {boolean} loading - Indicates if the API request is in progress
   */
  const [loading, setLoading] = useState(true);

  /**
   * @state {string} error - Error message if the API request fails, empty string otherwise
   */
  const [error, setError] = useState('');

  /**
   * Fetches all questionnaires from the API
   * @async
   * @function fetchQuestionnaires
   * @returns {Promise<void>}
   */
  const fetchQuestionnaires = async () => {
    try {
      const { data } = await api.get('questionnaires');
      // Access data.questionnaires and provide a fallback empty array
      setQuestionnaires(data.questionnaires || []);
    } catch (err) {
      setError('Erro ao buscar questionários');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Effect hook to fetch questionnaires when the component mounts
   * @effect
   */
  useEffect(() => {
    fetchQuestionnaires();
  }, []);

  /**
   * @typedef {Object} UseQuestionnairesReturn
   * @property {Array} questionnaires - The list of fetched questionnaires
   * @property {boolean} loading - Indicates if the API request is in progress
   * @property {string} error - Error message if the API request fails, empty string otherwise
   */

  /**
   * @returns {UseQuestionnairesReturn} The hook's return values
   */
  return { questionnaires, loading, error };
};
